import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { Board, Item } from '@/stores/canvasStore';
import { toLibraryCards } from '@/lib/library/libraryCard';
import { EMPTY_FILTER, filterLibraryCards, isFilterActive, type LibraryFilter } from '@/lib/library/search';
import { LIBRARY_SOURCES, isCanvasNote } from '@/lib/library/sources';
import { LibraryHeader, LibraryTabBar, type LibraryTab } from './LibraryChrome';
import { CONTENT_TOP, TAB_BAR_HEIGHT } from './chromeMetrics';
import { LibraryGrid } from './LibraryGrid';
import { LibrarySearchBar } from './LibrarySearchBar';
import { LibraryAddFlow, type AddMode } from './LibraryAddFlow';
import { LibrarySkeleton } from './LibrarySkeleton';
import { SpacesView } from './SpacesView';
import { LibraryProvider, NO_COMMENTS, type LibraryActions, type LibraryComments } from './LibraryContext';
import { LibraryDetailPresenceProvider } from './LibraryDetail';
import { cn } from '@/lib/utils';
import { usePullToRefresh } from './usePullToRefresh';
import { PullIndicator } from './PullIndicator';

export interface MobileLibraryProps {
  items: Item[];
  boards: Board[];
  actions: LibraryActions;
  /** Comments on items; defaults to none. */
  comments?: LibraryComments;
  /** Items just saved whose preview is still coming: their tiles develop. */
  pending?: ReadonlySet<string>;
  isLoading?: boolean;
  /** The account menu, rendered in the header's left slot. */
  accountSlot?: React.ReactNode;
  initialTab?: LibraryTab;
}

/**
 * Space the floating search pill and the tab bar take at the bottom. The feed
 * runs under both (they are glass); scroll areas pad by this
 * (var(--library-bottom-inset)) so the last row can still scroll clear.
 * Gap 12 + pill 56 + gap 12 above the tab bar's own height.
 */
const BOTTOM_INSET = `calc(80px + ${TAB_BAR_HEIGHT})`;
/** The fixed header's bottom edge: the page-scrolled feed starts below it. */
const TOP_INSET = CONTENT_TOP;

/**
 * The phone app: Everything (every saved item in one grid) and Spaces
 * (boards plus automatic spaces by source and subject), a floating search
 * pill over both, and + to add. Pure props in, so the dev fixture renders
 * it with no network; MobileLibraryContainer wires it to the store.
 *
 * The shell owns the tab and the filter. Each surface is its own file with
 * its own owner; see docs/mobile-library/plan.md.
 */
export function MobileLibrary({ items, boards, actions, comments = NO_COMMENTS, pending, isLoading = false, accountSlot, initialTab = 'everything' }: MobileLibraryProps) {
  const [tab, setTab] = useState<LibraryTab>(initialTab);
  const [filter, setFilter] = useState<LibraryFilter>(EMPTY_FILTER);
  // What + (or the pencil in the search pill) opened: the menu of ways to save, or a composer
  const [addMode, setAddMode] = useState<AddMode | null>(null);
  // The tab bar slides away while the search field is focused, so only the pill and its chips sit above the keyboard
  const [searchFocused, setSearchFocused] = useState(false);
  const reduceMotion = useReducedMotion();
  /** Bumped to reset Spaces to its list when the active Spaces tab is tapped again. */
  const [spacesKey, setSpacesKey] = useState(0);
  // A card's detail view is open: the search pill and tab bar step aside
  const [detailOpen, setDetailOpen] = useState(false);

  const cards = useMemo(() => toLibraryCards(items), [items]);
  // The feed is what you chose to keep: notes made on a canvas board stay in
  // their space and in search, not in Everything
  const feedCards = useMemo(() => cards.filter((c) => !isCanvasNote(c.item)), [cards]);
  // A typed search looks through everything; browsing and chips stay on the feed
  const visible = useMemo(() => filterLibraryCards(filter.query.trim() ? cards : feedCards, filter), [cards, feedCards, filter]);
  const availableSources = useMemo(() => {
    const present = new Set(feedCards.map((c) => c.source));
    return LIBRARY_SOURCES.filter((m) => present.has(m.key)).map((m) => m.key);
  }, [feedCards]);

  const handleFilterChange = (next: LibraryFilter) => {
    setFilter(next);
    // Search always searches everything
    if (isFilterActive(next) && tab !== 'everything') setTab('everything');
  };

  const filtering = isFilterActive(filter);
  const contextValue = useMemo(() => ({ actions, boards, comments, pending }), [actions, boards, comments, pending]);
  // Something was just saved: show Everything from the top, where it lands
  const handleSaved = () => {
    setFilter(EMPTY_FILTER);
    setTab('everything');
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  // Everything scrolls the page itself, so Safari tucks its address bar away
  // as you scroll (it only does for the page, never for a box inside it)
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('library-doc');
    return () => root.classList.remove('library-doc');
  }, []);
  // Pull down at the top of Everything to fetch what was saved elsewhere (the
  // share sheet, another device). Off while a card, the add flow or search is up.
  const rootRef = useRef<HTMLDivElement>(null);
  const { pull, refreshing } = usePullToRefresh(
    actions.refresh,
    rootRef,
    tab === 'everything' && !detailOpen && addMode === null && !searchFocused,
  );

  // A new tab or search starts at the top of the page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);

  return (
    <LibraryProvider value={contextValue}>
      <LibraryDetailPresenceProvider onChange={setDetailOpen}>
      {/* An app, not a page: no long-press text selection or callout on the chrome or grid (fields opt back in).
          The page scrolls (Everything); the header and the bottom bars are fixed over it. */}
      <div
        ref={rootRef}
        className="relative min-h-full select-none bg-paper text-ink [-webkit-touch-callout:none] [&_input]:select-text [&_textarea]:select-text"
        style={{ ['--library-bottom-inset' as string]: BOTTOM_INSET, ['--library-top-inset' as string]: TOP_INSET }}
      >
        {/* Solid paper: iOS colours the status bar from what sits at the top of the screen */}
        <div className="fixed inset-x-0 top-0 z-20 bg-paper">
          <LibraryHeader title={tab === 'spaces' && !filtering ? 'Spaces' : 'mindfull'} accountSlot={accountSlot} onAdd={() => setAddMode('menu')} />
        </div>

        <PullIndicator pull={pull} refreshing={refreshing} reduceMotion={reduceMotion ?? false} />

        {tab === 'everything' ? (
          <LibraryGrid
            cards={visible}
            scrollMode="document"
            emptyState={
              isLoading ? (
                <LibrarySkeleton />
              ) : (
              <div className="flex h-64 flex-col items-center justify-center gap-1 px-8 text-center">
                {filtering ? (
                  <>
                    <p className="text-[15px] text-ink-2">Nothing matches</p>
                    <p className="text-[13px] text-ink-3">Try fewer words or another filter.</p>
                  </>
                ) : (
                  <>
                    <p className="text-[15px] text-ink-2">Your mind is empty</p>
                    <p className="text-[13px] text-ink-3">Tap + to save a link, a photo or a note.</p>
                  </>
                )}
              </div>
              )
            }
          />
        ) : (
          // Spaces keep their own scroll, in the space between the header and the bottom of the screen
          <main className="fixed inset-x-0 bottom-0" style={{ top: TOP_INSET }}>
            <SpacesView key={spacesKey} cards={cards} feedCards={feedCards} boards={boards} />
          </main>
        )}

        <div
          className={cn(
            'pointer-events-none fixed inset-x-0 bottom-0 z-30 flex flex-col gap-3 transition-[opacity,transform,visibility] duration-200 ease-out motion-reduce:transition-opacity',
            detailOpen && 'invisible translate-y-4 opacity-0 motion-reduce:translate-y-0',
          )}
          aria-hidden={detailOpen || undefined}
        >
          <LibrarySearchBar
            filter={filter}
            onFilterChange={handleFilterChange}
            availableSources={availableSources}
            resultCount={visible.length}
            onFocusChange={setSearchFocused}
            onWriteNote={() => setAddMode('note')}
          />
          {/* Slides down out of the way (transform only) so nothing above it reflows while the keyboard rises */}
          <motion.div
            initial={false}
            animate={{ y: searchFocused ? '115%' : '0%', opacity: searchFocused ? 0 : 1 }}
            transition={reduceMotion ? { duration: 0 } : { y: { type: 'spring', stiffness: 500, damping: 38 }, opacity: { duration: 0.16 } }}
            aria-hidden={searchFocused || undefined}
          >
            <LibraryTabBar
              tab={tab}
              onTabChange={(next) => {
                // Tapping Spaces again while a space is open goes back to the list, like a native tab bar
                if (next === 'spaces' && tab === 'spaces') setSpacesKey((k) => k + 1);
                setTab(next);
                if (next === 'spaces') setFilter(EMPTY_FILTER);
              }}
            />
          </motion.div>
        </div>

        <LibraryAddFlow mode={addMode} onModeChange={setAddMode} onSaved={handleSaved} />
      </div>
      </LibraryDetailPresenceProvider>
    </LibraryProvider>
  );
}
